import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/atoms/input';
import { cn } from '@/lib/utils';

/**
 * NOT-241: a numeric field that commits on every keystroke, so figures derived
 * from it — a line subtotal, a markup percent, the document totals — track what
 * is on screen instead of lagging until the field loses focus.
 *
 * Committing bumps the parent value, which flows back down as a new `value` and
 * would re-fire the resync effect, clobbering a partial draft like "10." or
 * "1.5". The resync is therefore skipped while the field is focused; blur
 * normalizes the draft to the clamped, committed number.
 *
 * Only use this where `onCommit` is cheap (local state). Anything that fires a
 * request per keystroke wants a debounce instead.
 */
export interface LiveNumberInputProps {
  value: number;
  onCommit: (value: number) => void;
  min?: number;
  max?: number;
  step?: number | string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

function formatCommitted(value: number): string {
  return value ? String(value) : '';
}

export function LiveNumberInput({
  value,
  onCommit,
  min = 0,
  max,
  step,
  className,
  placeholder = '0',
  disabled,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}: LiveNumberInputProps) {
  const [draft, setDraft] = useState(() => formatCommitted(value));
  const focusedRef = useRef(false);
  // What an unparseable draft resolves back to on blur. Held here rather than
  // read off `value`, because the parent can change `value` underneath a focused
  // field (an external recalculation or a refetch) — blurring "2." must recover
  // the 2 this field last parsed, not an unrelated value that arrived meanwhile.
  const lastParsedRef = useRef(value);

  useEffect(() => {
    if (focusedRef.current) return;
    lastParsedRef.current = value;
    setDraft(formatCommitted(value));
  }, [value]);

  function parse(raw: string): number {
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function clamp(value: number): number {
    return Math.min(max ?? Infinity, Math.max(min, value));
  }

  return (
    <Input
      type="number"
      min={min}
      max={max}
      step={step}
      value={draft}
      placeholder={placeholder}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      className={cn(
        '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
        className,
      )}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onKeyDown={(event) => {
        // Keeps the sign from ever appearing on the common typing path. This is
        // only cosmetic — the authoritative guard is in onChange below, which
        // also covers paste, drag-drop and IME/mobile input that never produce
        // this keypress.
        if (min >= 0 && event.key === '-') event.preventDefault();
      }}
      onChange={(event) => {
        const raw = event.target.value;
        // A field floored at 0 cannot hold a negative — the consumers' state
        // clamps one to 0 (`useEstimateLineItemState`) — so accepting "-4" would
        // render a number the app never holds: the field reading -4 against a
        // correct $0.00 subtotal. Reject it before it reaches the draft, so the
        // rendered value is unchanged and React restores the input to it. Doing
        // this in the value path covers every route in — paste, drag-drop,
        // IME/mobile — not just the keypress. Fields that permit negatives
        // (Markup %, min -100) are unaffected.
        if (min >= 0 && parse(raw) < 0) return;
        setDraft(raw);
        // A number input reports value "" for a draft it cannot parse — "2." on
        // the way to "2.5", or a lone "-" — and flags it as `badInput`. Treating
        // that as 0 flashed a $0.00 subtotal mid-decimal (seen on staging), so
        // hold the last committed value until the draft parses again. A field the
        // user genuinely cleared reports "" WITHOUT `badInput`, and still commits 0.
        if (event.target.validity?.badInput) return;
        // Otherwise commit what is on screen, unclamped: a figure derived from the
        // field must never contradict the field. An overshoot (`150` in a 0-100
        // discount) is normalized once, on blur — and since nothing here is
        // persisted before a Save click, which fires blur first, an out-of-range
        // draft cannot escape the editor.
        const next = parse(raw);
        lastParsedRef.current = next;
        onCommit(next);
      }}
      onBlur={(event) => {
        focusedRef.current = false;
        // Leaving the field on an unparseable draft resolves it to the last value
        // that did parse ("2." → 2), never to 0.
        const committed = clamp(
          event.target.validity?.badInput ? lastParsedRef.current : parse(draft),
        );
        lastParsedRef.current = committed;
        onCommit(committed);
        setDraft(formatCommitted(committed));
      }}
    />
  );
}
